// docs: https://github.com/altostra/type-validations#readme
import { maybe, objectOf } from "@altostra/type-validations";
import { number, string } from "@altostra/type-validations/lib/primitives";

import type { CampaignBriefOperatorAttachedFile, GetCampaignBriefResult } from "./types";

/**
 * Operator needs every descriptor field to attach the file, so a partial or
 * mistyped descriptor is rejected here (as a payload-shape error) rather than
 * downstream. null/absent is valid: the campaign has no persisted brief.
 */
const isOperatorAttachedFileField = maybe(
  objectOf<CampaignBriefOperatorAttachedFile>({
    url: string,
    file_name: string,
    mime_type: string,
    context: string,
    expires_at: number,
    // Optional (absent or null on backends that predate it).
    file_updated_at: maybe(number, true),
  }),
  true
);

/**
 * Guard for the GET response. The brief is null when none is persisted;
 * otherwise it's a metadata object whose file_name (the one field the UI
 * renders) must be a string — lenient on the rest to tolerate additive drift.
 */
export const isGetCampaignBriefResult = (value: unknown): value is GetCampaignBriefResult => {
  if (value === null || typeof value !== "object") {
    return false;
  }
  if (!isOperatorAttachedFileField((value as { operator_attached_file?: unknown }).operator_attached_file)) {
    return false;
  }
  const brief = (value as { brief?: unknown }).brief;
  if (brief === null || brief === undefined) {
    return true;
  }
  return typeof brief === "object" && typeof (brief as { file_name?: unknown }).file_name === "string";
};
